import React, { useState, useEffect } from 'react';
import { X, Inbox, Play } from 'lucide-react';
import { Album, AlbumMedia } from '../../types';
import { apiService } from '../../services';

interface AlbumColumnProps {
  albums: Album[];
  loading: boolean;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    total: number;
  };
  onPageChange: (page: number) => void;
  onEdit: (album: Album) => void;
  onDelete: (album: Album) => void;
}

interface AlbumCardProps {
  album: Album;
  onEdit: () => void;
  onDelete: () => void;
}

const AlbumCard: React.FC<AlbumCardProps> = ({ album, onEdit, onDelete }) => {
  const [mediaItems, setMediaItems] = useState<AlbumMedia[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlbumMedia = async () => {
      try {
        const response = await apiService.getAlbumMedia(album.id);
        setMediaItems(response.media_items);
      } catch (error) {
        console.error('Failed to fetch album media:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlbumMedia();
  }, [album.id]);

  const getMediaThumbnail = (media: AlbumMedia) => {
    const mediaUrl = apiService.getAlbumMediaUrl(media.id);

    if (media.file_type === 'image') {
      return (
        <img
          src={mediaUrl}
          alt={media.title || media.filename}
          className='w-full h-48 object-cover rounded-lg'
          loading='lazy'
        />
      );
    } else {
      return (
        <div className='w-full h-48 bg-gray-900 flex items-center justify-center rounded-lg'>
          <div className='text-white text-center'>
            <Play className='w-10 h-10 mx-auto mb-2' />
            <p className='text-sm'>{media.title || 'Vidéo'}</p>
          </div>
        </div>
      );
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div className='bg-white rounded-lg shadow-md overflow-hidden border border-gray-200'>
      {/* Album Header */}
      <div className='bg-gray-50 px-6 py-4 border-b relative'>
        <button
          onClick={handleDelete}
          className='absolute top-4 right-4 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10'
          title='Supprimer'
        >
          <X className='w-4 h-4' />
        </button>
        <div className='flex items-center justify-between mb-2 pr-10'>
          <h2
            className='text-xl font-semibold text-gray-900 cursor-pointer hover:text-blue-600'
            onClick={onEdit}
          >
            {album.name}
          </h2>
        </div>
        {album.description && (
          <p className='text-gray-600 text-sm mt-1'>{album.description}</p>
        )}
        <div className='flex items-center gap-4 mt-2 text-xs text-gray-500'>
          <span
            className={`px-2 py-1 rounded ${
              album.status === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {album.status}
          </span>
          <span>{album.media_count} média(s)</span>
          {album.author && (
            <span>Par {album.author.full_name || album.author.username}</span>
          )}
          {album.published_at && (
            <span>
              {new Date(album.published_at).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      </div>

      {/* Album Content */}
      <div className='p-6'>
        {loading ? (
          <div className='flex justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
          </div>
        ) : mediaItems.length === 0 ? (
          <div className='text-center py-8 text-gray-500'>
            <Inbox className='w-10 h-10 mx-auto mb-2 text-gray-300' />
            <p>Cet album ne contient aucun média</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
            {mediaItems.map((media) => (
              <div
                key={media.id}
                className='rounded-lg overflow-hidden shadow-sm border border-gray-200'
              >
                {getMediaThumbnail(media)}
                {(media.title || media.description) && (
                  <div className='p-3'>
                    {media.title && (
                      <h4 className='text-sm font-medium text-gray-900 mb-1'>
                        {media.title}
                      </h4>
                    )}
                    {media.description && (
                      <p className='text-xs text-gray-600 line-clamp-2'>
                        {media.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const AlbumColumn: React.FC<AlbumColumnProps> = ({
  albums,
  loading,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
}) => {
  const totalPages = Math.ceil(pagination.total / pagination.itemsPerPage);

  if (loading) {
    return (
      <div className='flex justify-center items-center py-16'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-gray-600'>Chargement...</p>
        </div>
      </div>
    );
  }

  if (albums.length === 0) {
    return (
      <div className='text-center py-16'>
        <Inbox className='w-16 h-16 mx-auto mb-4 text-gray-300' />
        <h2 className='text-xl font-semibold text-gray-900 mb-2'>
          Aucun album trouvé
        </h2>
        <p className='text-gray-600'>
          Aucun album ne correspond aux critères de recherche.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Albums Column */}
      <div className='space-y-8 mb-6'>
        {albums.map((album) => (
          <AlbumCard
            key={album.id}
            album={album}
            onEdit={() => onEdit(album)}
            onDelete={() => onDelete(album)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-center gap-2 mt-6'>
          <button
            onClick={() => onPageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            Précédent
          </button>

          <div className='flex gap-1'>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - pagination.currentPage) <= 1) return true;
                return false;
              })
              .map((page, index, array) => {
                const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                return (
                  <React.Fragment key={page}>
                    {showEllipsisBefore && (
                      <span className='px-2 py-2 text-gray-500'>...</span>
                    )}
                    <button
                      onClick={() => onPageChange(page)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        pagination.currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => onPageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === totalPages}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            Suivant
          </button>
        </div>
      )}

      {/* Pagination Info */}
      <div className='text-center text-sm text-gray-600 mt-4'>
        Page {pagination.currentPage} sur {totalPages} • {pagination.total} album(s)
        au total
      </div>
    </div>
  );
};
